import type { CSSProperties } from 'react'
import type { LiffTheme } from '@/types'

/**
 * The LIFF login page's theme as the CSS custom properties
 * `styles/liff-login.css` reads, for the Settings > LINE preview.
 *
 * A twin of `liff/src/lib/loginTheme.ts` (the two apps share no code package,
 * and the stylesheet is copied the same way): keep the two in step, so the
 * preview shows what the LINE login page will show. Unset or unusable values
 * leave their variable unset and the stylesheet's original look applies.
 * Alignment comes from a fixed list and the sizes are clamped whole pixels,
 * because the values go into CSS.
 */
export const LOGIN_TITLE_ALIGNS = ['left', 'center', 'right'] as const
/** 2026-10-01 (owner): the panel's distance from the screen edge is its own
 * setting, so a wide panel padding no longer pushes it to the edge. */
export const LOGIN_PANEL_MARGIN_MAX_PX = 48
export const LOGIN_PANEL_PADDING_MAX_PX = 24
export const LOGIN_PANEL_RADIUS_MAX_PX = 32

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

function pixels(value: string | undefined, max: number): string | undefined {
  if (value === undefined || value.trim() === '') return undefined
  const n = Number(value)
  if (!Number.isFinite(n)) return undefined
  return `${Math.min(Math.max(Math.round(n), 0), max)}px`
}

function titleAlign(value: string | undefined): string | undefined {
  return (LOGIN_TITLE_ALIGNS as readonly string[]).includes(value ?? '') ? value : undefined
}

/** `bgImageUrl` is the resolved background image URL, or undefined for none. */
export function liffLoginPreviewVars(theme: LiffTheme, bgImageUrl: string | undefined): CSSProperties {
  const padding = pixels(theme.login_panel_padding, LOGIN_PANEL_PADDING_MAX_PX)
  return {
    '--login-bg-color': theme.login_bg_color,
    '--login-bg-image': bgImageUrl ? `url(${bgImageUrl})` : 'none',
    '--login-button-color': theme.button_color,
    '--login-panel-color': theme.login_panel_enabled === 'true'
      ? hexToRgba(theme.login_panel_color || '#ffffff', Number(theme.login_panel_opacity))
      : 'transparent',
    '--login-title-align': titleAlign(theme.login_title_align),
    '--login-panel-margin': pixels(theme.login_panel_margin, LOGIN_PANEL_MARGIN_MAX_PX),
    '--login-panel-pad-x': padding,
    '--login-panel-pad-y': padding,
    '--login-panel-radius': pixels(theme.login_panel_radius, LOGIN_PANEL_RADIUS_MAX_PX),
  } as CSSProperties
}
